import { type To } from 'react-router-dom'

import {
  DateRangeType,
  FREE_PERIODS_STOCK,
  SpecificChartFunctionality,
  TIMEFRAME,
  TimeframeAndIntradayType,
} from '../../constants/common'
import { DATE_RANGE_PREFIX_DELMITER } from '../../utils/chart-date-range-utils'
import { CustomDateRangePrefixEnum } from '../../utils/chart-date-range-utils'
import { CUSTOM_DATE_RANGE } from '../../utils/chart-date-range-utils'
import { decodeQueryString, encodeQueryString } from '../../utils/query_string'

interface ITimeFrame {
  value: TimeframeAndIntradayType
  label: string
  labelShort: string
  labelPageTitle?: string
}

interface ITimeframeGroup {
  label: string
  timeframes: ITimeFrame[]
}

interface IDateRange {
  value: string
  label: string
  timeframe?: TIMEFRAME
}

export const getTimeFrameGroups = (isPremium = true): ITimeframeGroup[] => {
  const premiumTimeframes: ITimeframeGroup[] = [
    {
      label: '',
      timeframes: [
        { value: TIMEFRAME.i1, label: '1 Minute', labelShort: '1M' },
        { value: TIMEFRAME.i2, label: '2 Minutes', labelShort: '2M' },
        { value: TIMEFRAME.i3, label: '3 Minutes', labelShort: '3M' },
        { value: TIMEFRAME.i5, label: '5 Minutes', labelShort: '5M' },
        { value: TIMEFRAME.i10, label: '10 Minutes', labelShort: '10M' },
        { value: TIMEFRAME.i15, label: '15 Minutes', labelShort: '15M' },
        { value: TIMEFRAME.i30, label: '30 Minutes', labelShort: '30M' },
      ],
    },
    {
      label: '',
      timeframes: [
        { value: TIMEFRAME.h, label: '1 Hour', labelShort: '1H', labelPageTitle: 'Hourly' },
        { value: TIMEFRAME.h2, label: '2 Hours', labelShort: '2H' },
        { value: TIMEFRAME.h4, label: '4 Hours', labelShort: '4H' },
      ],
    },
    {
      label: '',
      timeframes: [
        { value: TIMEFRAME.d, label: '1 Day', labelShort: 'D', labelPageTitle: 'Daily' },
        { value: TIMEFRAME.w, label: '1 Week', labelShort: 'W', labelPageTitle: 'Weekly' },
        { value: TIMEFRAME.m, label: '1 Month', labelShort: 'M', labelPageTitle: 'Monthly' },
      ],
    },
  ]

  const freeTimeframes = [
    {
      // intradayTimeframe is only for non premium users used as redirect to elite subscription
      label: '',
      timeframes: [{ value: 'intraday' as const, label: 'Intraday', labelShort: 'Intraday' }],
    },
    {
      label: '',
      timeframes: [
        { value: TIMEFRAME.d, label: '1 Day', labelShort: 'Daily' },
        { value: TIMEFRAME.w, label: '1 Week', labelShort: 'Weekly' },
        { value: TIMEFRAME.m, label: '1 Month', labelShort: 'Monthly' },
      ],
    },
  ]

  return isPremium ? premiumTimeframes : freeTimeframes
}

export const getDateRangeGroups = (isPremium = true) => {
  const rangeGroups: Array<{ label?: string; dateRanges: IDateRange[] }> = [
    {
      label: 'Days',
      dateRanges: [
        { value: DateRangeType.d1, label: '1 Day', timeframe: TIMEFRAME.i1 },
        { value: DateRangeType.d5, label: '5 Days', timeframe: TIMEFRAME.i5 },
      ],
    },
    {
      label: 'Months',
      dateRanges: [
        { value: DateRangeType.m1, label: '1 Month', timeframe: TIMEFRAME.d },
        { value: DateRangeType.m3, label: '3 Months', timeframe: TIMEFRAME.d },
        { value: DateRangeType.m6, label: '6 Months', timeframe: TIMEFRAME.d },
      ],
    },
    {
      label: 'Years',
      dateRanges: [
        { value: DateRangeType.ytd, label: 'Year to Date', timeframe: TIMEFRAME.d },
        { value: DateRangeType.y1, label: '1 Year', timeframe: TIMEFRAME.d },
        { value: DateRangeType.y2, label: '2 Years', timeframe: TIMEFRAME.w },
        { value: DateRangeType.y5, label: '5 Years', timeframe: isPremium ? TIMEFRAME.w : TIMEFRAME.m },
        { value: DateRangeType.max, label: 'Max', timeframe: TIMEFRAME.m },
      ],
    },
  ]

  return rangeGroups
}

const timeframeUtm: Partial<Record<SpecificChartFunctionality, string>> = {
  [SpecificChartFunctionality.quotePage]: 'quote-timeframe',
  [SpecificChartFunctionality.futuresPage]: 'futures-timeframe',
  [SpecificChartFunctionality.forexPage]: 'forex-timeframe',
  [SpecificChartFunctionality.cryptoPage]: 'crypto-timeframe',
}

export function generatePathForTimeframeDateRange({
  isPremium,
  pathname,
  timeframe,
  dateRange,
  specificChartFunctionality,
  freeTimeframes = FREE_PERIODS_STOCK,
}: {
  isPremium: boolean
  pathname: string
  timeframe: TimeframeAndIntradayType
  dateRange: string | null | undefined
  specificChartFunctionality: SpecificChartFunctionality
  freeTimeframes?: string[]
}) {
  if (isPremium || freeTimeframes.includes(timeframe)) {
    return {
      pathname,
      search: `?${encodeQueryString({
        ...decodeQueryString(),
        i: undefined,
        p: timeframe,
        r: dateRange ?? undefined,
      })}`,
    }
  }

  return {
    pathname: '/elite.ashx',
    search: `?${encodeQueryString({
      utm_source: 'finviz',
      utm_medium: 'banner',
      utm_campaign: timeframeUtm[specificChartFunctionality],
    })}`,
  }
}

export function generateTimeframeGroups(isPremium: boolean, generatePath: (timeframe: ITimeFrame) => To) {
  return getTimeFrameGroups(isPremium).map((timeFrameGroup) => ({
    label: timeFrameGroup.label,
    timeframes: timeFrameGroup.timeframes.map((timeFrame) => ({
      ...timeFrame,
      href: generatePath(timeFrame),
    })),
  }))
}

export function generateDateRangeGroups(
  isPremium: boolean,
  customRange: string | undefined,
  generatePath: (timeframe: IDateRange) => To
) {
  const rangeGroups = getDateRangeGroups(isPremium)
  if (isPremium) {
    const [dateRangePrefix] = customRange?.split(DATE_RANGE_PREFIX_DELMITER) ?? []
    rangeGroups.push({
      dateRanges: [
        {
          value:
            customRange && Object.values<string>(CustomDateRangePrefixEnum).includes(dateRangePrefix)
              ? customRange
              : CUSTOM_DATE_RANGE,
          label: 'Custom',
        },
      ],
    })
  }

  return rangeGroups.map((dateRangeGroup) => ({
    label: dateRangeGroup.label,
    dateRanges: dateRangeGroup.dateRanges.map((dateRange) => ({
      ...dateRange,
      href:
        !isPremium || Object.values(DateRangeType).includes(dateRange.value as DateRangeType)
          ? generatePath(dateRange)
          : null,
    })),
  }))
}

export enum QuoteNodeChartTypes {
  candlesticksimple = 'candlesticksimple',
  candlestick = 'candlestick',
  line = 'line',
  ohlc = 'ohlc',
  heikinashi = 'heikinashi',
  hollowcandlestick = 'hollowcandlestick',
}

export enum QuoteUrlChartTypes {
  cs = 'cs',
  c = 'c',
  l = 'l',
  o = 'o',
  ha = 'ha',
  hc = 'hc',
}

export const QuoteShortToLongType = {
  [QuoteUrlChartTypes.cs]: QuoteNodeChartTypes.candlesticksimple,
  [QuoteUrlChartTypes.c]: QuoteNodeChartTypes.candlestick,
  [QuoteUrlChartTypes.l]: QuoteNodeChartTypes.line,
  [QuoteUrlChartTypes.o]: QuoteNodeChartTypes.ohlc,
  [QuoteUrlChartTypes.ha]: QuoteNodeChartTypes.heikinashi,
  [QuoteUrlChartTypes.hc]: QuoteNodeChartTypes.hollowcandlestick,
} as const

export const QuoteLongToShortType = {
  [QuoteNodeChartTypes.candlesticksimple]: QuoteUrlChartTypes.cs,
  [QuoteNodeChartTypes.candlestick]: QuoteUrlChartTypes.c,
  [QuoteNodeChartTypes.line]: QuoteUrlChartTypes.l,
  [QuoteNodeChartTypes.ohlc]: QuoteUrlChartTypes.o,
  [QuoteNodeChartTypes.heikinashi]: QuoteUrlChartTypes.ha,
  [QuoteNodeChartTypes.hollowcandlestick]: QuoteUrlChartTypes.hc,
} as const

export const DEFAULT_TYPES = [
  { iconName: 'candleBarBasic', value: QuoteNodeChartTypes.candlestick, label: 'Candle' },
  { iconName: 'lineChartType', value: QuoteNodeChartTypes.line, label: 'Line' },
  { iconName: 'ohlcChartType', value: QuoteNodeChartTypes.ohlc, label: 'OHLC' },
  { iconName: 'candleBarHollow', value: QuoteNodeChartTypes.hollowcandlestick, label: 'Hollow Candle' },
  { iconName: 'candleBarHeikinAshi', value: QuoteNodeChartTypes.heikinashi, label: 'Heikin Ashi' },
]

export const FREE_TYPES_QUOTE = [
  { iconName: 'candleBarBasic', value: 'candlesticksimple', label: 'Candle - simple' },
  { iconName: 'candleBarBasic', value: QuoteNodeChartTypes.candlestick, label: 'Candle - advanced' },
  // Filter out the candle from DEFAULT_TYPES, we’re naming it differently
  ...DEFAULT_TYPES.filter((type) => type.value !== QuoteNodeChartTypes.candlestick),
]

export const FREE_TYPES_QUOTE_VALUES = ['candlesticksimple', 'candlestick', 'line']

export type TypeBaseOption = {
  label: string
  value: string
  iconName: string
  href?: string
}
